import React from "react";
import {
  type GetThemeValueForKey,
  Text,
  type TextProps,
  XStack,
} from "tamagui";
import type { IconProp } from "./icons";
import { Subtext } from "./Subtext";

const bodyStyle = {
  fontFamily: "$subtextLabel",
} as const;

export enum PillType {
  Success = "success",
  Info = "info",
  Danger = "danger",
  Primary = "primary",
  Muted = "muted",
}

const bg = {
  [PillType.Success]: "$color.success",
  [PillType.Info]: "$color.info",
  [PillType.Danger]: "$color.danger",
  [PillType.Primary]: "$pressablePrimaryBackground",
  [PillType.Muted]: "rgba(0,0,0,.05)",
};

const label = {
  [PillType.Success]: "$inverted",
  [PillType.Info]: "$inverted",
  [PillType.Danger]: "$inverted",
  [PillType.Primary]: "$pressablePrimaryLabel",
  [PillType.Muted]: "$inverted",
};

type PillProps = React.PropsWithChildren<
  { type: PillType } & Omit<TextProps, "color">
>;

export const Pill = ({ children, type, ...props }: PillProps) => {
  return (
    <Text
      {...bodyStyle}
      alignSelf="flex-start"
      borderRadius={100}
      paddingHorizontal="$medium"
      paddingVertical="$small"
      numberOfLines={1}
      // biome-ignore lint/suspicious/noExplicitAny: Because
      color={label[type] as any}
      // biome-ignore lint/suspicious/noExplicitAny: Because
      backgroundColor={bg[type] as any}
      {...props}
    >
      {children}
    </Text>
  );
};

export const IconPill = ({
  icon,
  text,
  textColor = "$color",
  ...props
}: Omit<PillProps, "children" | "text"> & {
  text: string;
  textColor?: string;
  icon: IconProp;
}) => {
  const labelColor = label[props.type] ?? textColor;

  const Icon = icon;
  return (
    <Pill {...props}>
      <XStack gap="$space.small" alignItems="center" cursor="pointer">
        <Icon size={16} color={labelColor as GetThemeValueForKey<"color">} />
        <Subtext color={labelColor as GetThemeValueForKey<"color">}>
          {text}
        </Subtext>
      </XStack>
    </Pill>
  );
};
